import Footer from "../components/footer/Footer";
import {StoreContext} from "../stores/StoreLoader";
import * as React from "react";
import NotificationContainer from "../components/blocks/SchoolBlocks/Notification/NotificationContainer";
import UserMenu from "../components/header/UserMenu";
import dynamic from "next/dynamic";
import {ErrorBoundary, rollbarConfig} from "../components/utilities/ErrorBoundary";
import {Provider} from '@rollbar/react';
import {getQueries} from "../utils/SchoolBlocksUtilities";
import {useContext, useMemo} from "react";
import SafeRouter from "../components/utilities/SafeRouter";
import {withoutLocaleInURI} from "../internationalization/i18nURI";
import URI from 'urijs';
import {NotificationContainer as ReactNotificationContainer} from '../components/notifications/NotificationContainer';
import ClickableLink from "../components/utilities/ClickableLink";
import classNames from "classnames";
import {WebAppInstallPrompt} from "../components/header/WebAppInstallPrompt";
import {observer} from "mobx-react";
import {IView} from "../stores/InterfaceStore";
import Header from "../components/header/Header";
import {useHeroTerrace} from "../hooks/useHeroTerrace";
import {useGridType} from "../hooks/useGridType";
import {GridTypes} from "./MainGridPage";
import {HeaderShortcuts} from "../components/header/HeaderShortcuts";
import {MobileNavFooter} from "../components/header/navigation/MobileNav/MobileNavFooter";

const pages = {
    [IView.DEFAULT]: dynamic(() => import("./MainGridPage")),
    [IView.ALPHA]: dynamic(() => import("./AlphaCollectionPage")),
    [IView.MAP]: dynamic(() => import("./MapFixedCollectionPage")),
    [IView.CLASSROOM]: dynamic(() => import("./MainGridPage")),
    [IView.CALENDAR]: dynamic(() => import("./CalendarPage")),
    [IView.MYCALENDAR]: dynamic(() => import("./CalendarPage")),
    [IView.PAGE]: dynamic(() => import("./PageBlockPage/PageBlockPage")),
    [IView.FOLLOWING]: dynamic(() => import("../../components/components/admin/sidebar/pages/FollowingPage")),
    [IView.FEED]: dynamic(() => import("./FeedGridPage")),
    [IView.SEARCH]: dynamic(() => import("./SearchGridPage")),
    [IView.ERROR]: dynamic(() => import("./ErrorPage")),
    [IView.LOGIN]: dynamic(() => import("./LoginPage")),
    [IView.USER_SETTINGS]: dynamic(() => import("./UserSettings/UserSettingsPage")),
    [IView.ORGANIZATION_SETTINGS]: dynamic(() => import("./OrganizationSettings/OrganizationSettings")),
    [IView.ORGANIZATION_PAGE]: dynamic(() => import("./ViewOrganization")),
    [IView.SCHOOLFEED_PAGE]: dynamic(() => import("./SchoolFeedFullPage")),
    [IView.SPLAINER_PAGE]: dynamic(() => import("../../components/components/schoolfeed/SchoolFeedSplainerPage")),
    [IView.NOTIFICATION]: dynamic(() => import("./ViewNotificationPage")),
}

const WelcomePage = dynamic(() => import("./WelcomePage"));
const AdminMenu = dynamic(() => import("../components/misc/AdminMenu"));
const SchoolFeedWidget = dynamic(() => import("../components/schoolfeed/SchoolFeedWidget"));

const showAdminMenu = ["default", "alpha", "map-fixed", "classroom", "calendar", "page", "organization"];

export const useShowHomepage = (() => {
    const {organizationStore, interfaceStore} = useContext(StoreContext);
    const queries = getQueries(interfaceStore.currentFullUrl || "");
    return (interfaceStore.view === IView.DEFAULT &&
        organizationStore.organization.json_data?.settings?.appearance?.showWelcomePage &&
        URI(withoutLocaleInURI(interfaceStore.currentFullUrl || "")).path() === "/" &&
        (!queries.showHomepage || queries.showHomepage === "false")) as boolean;
})

export const SchoolBlocksPageWrapper = observer((props: {
    view: string,
    notifications: IAlert[],
    user: IUser,
}): JSX.Element => {
    const {userStore, organizationStore, interfaceStore, styleStore, gridStore} = useContext(StoreContext);

    const gridType = useGridType();

    const showSchoolFeed = gridType !== GridTypes.terraced &&
        (organizationStore.organization.json_data?.settings?.appearance?.showSchoolFeedWidget === "everyone" ||
            (organizationStore.organization.json_data?.settings?.appearance?.showSchoolFeedWidget === "admins" && userStore.isAdmin)) && props.view !== IView.SCHOOLFEED_PAGE;

    let notifications = props.notifications;
    if (props.view === IView.NOTIFICATION) {
        // if user is here to view a notification, don't show any others
        notifications = [];
    }

    let content;
    const showHomePage = useShowHomepage()
    if (showHomePage) {
        content = <div id={"welcome-page-container"}>
            <WelcomePage handleViewHomepage={async function () {
                await SafeRouter.setQuery("showHomepage", "true");
            }}/>
        </div>
    } else {
        let ContentComponent = pages[props.view];

        if (!ContentComponent) {
            ContentComponent = pages.error;
        } else if (props.view === IView.ORGANIZATION_SETTINGS && !userStore.isAdmin) {
            ContentComponent = pages[IView.LOGIN];
        }

        let isIOS, isAndroidChrome;
        if (interfaceStore.ExecutionEnvironment.canUseDOM) {
            if (/iPad|iPhone|iPod/g.test(navigator.userAgent) && !window.MSStream) {
                isIOS = true;
                isAndroidChrome = false;
            } else if (/Android.*Chrome/g.test(navigator.userAgent) && !window.MSStream) {
                isIOS = false;
                isAndroidChrome = true;
            } else {
                isIOS = false;
                isAndroidChrome = false;
            }
        }

        const wrapperClassName = classNames({
            "isIOS": isIOS,
            "isAndroidChrome": isAndroidChrome,
            "isNotAndroidChrome": !isAndroidChrome,
            "isNotIOS": !isIOS,
            "sb-theme-simple_and_clean": styleStore.themeID === "1",
            "sb-theme-floating_blocks": styleStore.themeID === "2",
            "sb-theme-minimal": styleStore.themeID === "3",
            "sb-terraced-grid": gridType === GridTypes.terraced && props.view === IView.DEFAULT,
        })

        const heroTerrace = useHeroTerrace();
        const displayHero = !!(gridType === GridTypes.terraced &&
            props.view === IView.DEFAULT && heroTerrace);

        content = <div className={wrapperClassName}>
            <div className={"sb-navigation-shown"}>
                <div id={"sb-content-canvas"}>
            <span id="skiptocontent">
                <ClickableLink href="#sb-main-content" tabIndex={2}>skip to main content</ClickableLink>
                {showSchoolFeed && <ClickableLink href="#schoolfeed" tabIndex={2}
                               onClick={() => interfaceStore.toggleSchoolFeedWidgetOpen()}>
                    skip to schoolfeed
                </ClickableLink>}
                <ClickableLink href={(interfaceStore.breakpoint === "break-point-lg") ? "#navigation" : "#MobileNavigationMenu"} tabIndex={2}>skip to navigation</ClickableLink>
            </span>
                    <ReactNotificationContainer/>
                    {organizationStore.organization.json_data?.settings?.webapp?.enabled && (interfaceStore.view !== IView.SPLAINER_PAGE) && <ErrorBoundary>
                        <WebAppInstallPrompt user={props.user}/>
                    </ErrorBoundary>}
                    <ErrorBoundary>
                        <NotificationContainer notifications={notifications}
                                               shouldShowOverlay={true}
                                               heroIsEnabled={displayHero}
                                               key={organizationStore.currentOrganization.id}/>
                    </ErrorBoundary>
                    {interfaceStore.view === IView.DEFAULT && <ErrorBoundary>
                        <HeaderShortcuts />
                    </ErrorBoundary>}
                    <ErrorBoundary>
                        <Header displayHero={displayHero}/>
                    </ErrorBoundary>
                    <ErrorBoundary>
                        <NotificationContainer notifications={notifications}
                                               shouldShowBanner={true}
                                               key={organizationStore.currentOrganization.id}/>
                    </ErrorBoundary>
                    <div className="user-menu-container"><ErrorBoundary>
                        <UserMenu/></ErrorBoundary>
                    </div>
                    <ErrorBoundary>
                        <ContentComponent {...props} />
                    </ErrorBoundary>
                    {showSchoolFeed && <ErrorBoundary>
                        <SchoolFeedWidget organization_id={organizationStore.currentOrganization.id}/>
                    </ErrorBoundary>}
                    <ErrorBoundary>
                        <Footer/>
                        <MobileNavFooter showSchoolFeed={showSchoolFeed}/>
                    </ErrorBoundary>
                    {userStore.editor && showAdminMenu.includes(props.view) && <ErrorBoundary>
                        <AdminMenu key={interfaceStore.currentFullUrl} />
                    </ErrorBoundary>}
                </div>
            </div>
        </div>
    }

    if (userStore.id) {
        rollbarConfig.payload.person = {
            id: userStore.id,
        }
    }

    return <Provider config={rollbarConfig}>
        {content}
    </Provider>
})
